/** Visor de auditoría (spec 14 regla 5): filtros por usuario, sucursal, evento, entidad y rango; diff antes/después legible; export XLSX. */
import type { SucursalDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { EstadoQuery } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { claves } from '@/lib/query.ts';
import { veTasa2 } from '@/lib/tasa2.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { descargarArchivo } from '@/modulos/compras/api.ts';

interface Entrada {
  id: number;
  ocurrido_en: string;
  usuario_nombre: string | null;
  sucursal_codigo: string | null;
  evento: string;
  entidad: string;
  entidad_id: string | null;
  antes: unknown;
  despues: unknown;
  detalle: unknown;
}
interface Pagina {
  datos: Entrada[];
  total: number;
  pagina: number;
  limite: number;
}
interface UsuarioMin { id: string; nombre: string }

/**
 * Nombre en pantalla de los datos más comunes de la auditoría (re-auditoría 2026-10-18, ronda 2, hallazgo 7-H13). Lo
 * que no está aquí se muestra con su clave tal cual.
 */
const ETIQUETA_DATO: Record<string, string> = {
  // Usuarios y sesiones
  nombre: 'Nombre', email: 'Correo', rol: 'Rol', activo: 'Activo', activa: 'Activa', sucursal_id: 'Sucursal', sucursal_nombre: 'Sucursal', password_cambiado: 'Contraseña cambiada',
  ultimo_login: 'Último acceso', bloqueado_hasta: 'Bloqueado hasta', creado_en: 'Creado el', motivo: 'Motivo',
  // Envíos por correo (pedido de la dueña 2026-10-19)
  para: 'Para', asunto: 'Asunto', resend_id: 'Id del envío', documento_tipo: 'Tipo de documento',
  // Configuración, empresa y estructura
  clave: 'Parámetro', valor: 'Valor', codigo: 'Código', serie: 'Serie', serie_manual: 'Serie del talonario manual', direccion_fiscal: 'Dirección fiscal', direccion: 'Dirección',
  telefono: 'Teléfono', correo: 'Correo', razon_social: 'Razón social', rif: 'RIF', formato_impresion: 'Papel de factura', plantilla_factura: 'Medidas del papel', principal: 'Principal',
  fondo_usd: 'Fondo $', fondo_bs: 'Fondo Bs', punto_banco: 'Banco del punto', modalidad: 'Modalidad', formato_despacho: 'Formato de despacho', desde: 'Desde', hasta: 'Hasta', nota: 'Nota',
  ultimo: 'Último número', ultimo_control: 'Último control', proximo_control: 'Próximo control', bcv: 'Tasa BCV', paralela: 'Tasa 2', factor_divisa: 'Factor divisa',
  // Documentos, cobros y caja
  documento: 'Documento', numero: 'Número', control: 'N° de control', tipo: 'Tipo', estado: 'Estado', estado_documento: 'Estado del documento', cliente: 'Cliente', lineas: 'Renglones',
  almacen: 'Almacén', total: 'Total', total_usd: 'Total $', total_bs: 'Total Bs', descuento: 'Descuento', condicion: 'Condición', por_cobrar: 'Por cobrar', aplicado: 'Aplicado',
  metodos: 'Métodos', metodo: 'Método', monto: 'Monto', monto_usd: 'Monto $', monto_bs: 'Monto Bs', moneda: 'Moneda', referencia: 'Referencia', igtf_usd: 'IGTF $', igtf_divisa: 'IGTF en divisa',
  igtf_base_usd: 'Base del IGTF $', vuelto_usd: 'Vuelto $', vuelto_bs: 'Vuelto Bs', vuelto_bs_metodo: 'Método del vuelto en Bs', sobrante_usd: 'Sobrante $', saldo_despues: 'Saldo después',
  caja: 'Caja', caja_id: 'Caja', cajero: 'Cajero', turno: 'Turno', tasa: 'Tasa', conteo: 'Conteo', retiros: 'Retiros', cobrado_usd: 'Cobrado $', diferencia_usd: 'Diferencia $',
  diferencia_abs_usd: 'Diferencia sin signo $', justificacion: 'Justificación', aprobado: 'Aprobado', autorizacion_id: 'Autorización', fecha: 'Fecha', periodo: 'Período',
  comprobante: 'Comprobante', recibido: 'Recibido', observacion: 'Observación', comentario: 'Comentario', impresiones: 'Impresiones',
  // Inventario, compras y precios
  producto: 'Producto', cantidad: 'Cantidad', existencia: 'Existencia', pendiente_entrega: 'Por entregar', minimo: 'Mínimo', precio_divisa: 'Precio $', costo_unitario: 'Costo',
  factura: 'Factura', compra: 'Compra', proveedor: 'Proveedor', neto: 'Neto', retencion_iva: 'Retención de IVA', retencion_islr: 'Retención de ISLR', categoria: 'Categoría',
  descripcion: 'Descripción', limite_credito_usd: 'Límite de crédito $', plazo_dias: 'Plazo (días)', lista_id: 'Lista de precios',
};
const etiquetaDato = (k: string) => ETIQUETA_DATO[k] ?? k;
/**
 * Datos que dejan ver la tasa 2 o el factor (la tasa paralela y el factor de los cambios de tasa, el factor de una
 * cotización re-tasada, la tasa 2 de una declaración de cierre): quien no es la dueña ve que cambiaron, no el número
 * (pedido de la dueña 2026-10-19).
 */
const DATOS_SOLO_DUENA = new Set(['paralela', 'tasa_paralela', 'factor_divisa', 'tasa']);
const SOLO_DUENA = '(solo la dueña)';
/** Valor tal como se guardó (compacto); lo que falta, con una raya; lo reservado, sin el número. */
const valorDato = (v: unknown, k?: string, veTasa = true) => (v === undefined ? '—' : !veTasa && k !== undefined && DATOS_SOLO_DUENA.has(k) && v !== null ? SOLO_DUENA : JSON.stringify(v));

/** Diff legible: claves que cambian entre antes y después; el resto se muestra compacto. */
function Diff({ antes, despues, detalle, veTasa }: { antes: unknown; despues: unknown; detalle: unknown; veTasa: boolean }) {
  const a = antes && typeof antes === 'object' ? (antes as Record<string, unknown>) : null;
  const d = despues && typeof despues === 'object' ? (despues as Record<string, unknown>) : null;
  if (a && d) {
    const claves = [...new Set([...Object.keys(a), ...Object.keys(d)])].filter((k) => JSON.stringify(a[k]) !== JSON.stringify(d[k]));
    if (claves.length > 0) {
      return (
        <ul className="space-y-0.5 text-[11px]">
          {claves.map((k) => <li key={k}><b>{etiquetaDato(k)}</b>: <span className="text-mg-rojo line-through">{valorDato(a[k], k, veTasa)}</span> → <span className="text-mg-verde">{valorDato(d[k], k, veTasa)}</span></li>)}
        </ul>
      );
    }
  }
  const v = despues ?? detalle ?? antes;
  if (v === null || v === undefined) return <span className="text-muted-foreground">—</span>;
  // Un objeto (alta, impresión, cierre…) se lee dato por dato con su nombre; listas y valores sueltos, compactos
  if (typeof v === 'object' && !Array.isArray(v)) {
    return (
      <p className="max-w-md break-words text-[11px] leading-tight text-muted-foreground">
        {Object.entries(v as Record<string, unknown>).map(([k, x], i) => <span key={k}>{i > 0 ? ' · ' : ''}<b>{etiquetaDato(k)}</b>: {valorDato(x, k, veTasa)}</span>)}
      </p>
    );
  }
  return <pre className="max-w-md whitespace-pre-wrap break-all text-[11px] leading-tight text-muted-foreground">{JSON.stringify(v, null, 0)}</pre>;
}

export function Auditoria() {
  const { usuario, puede } = useAuth();
  const [evento, setEvento] = useState('');
  const [entidad, setEntidad] = useState('');
  const [usuarioId, setUsuarioId] = useState('');
  const [sucursalId, setSucursalId] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [pagina, setPagina] = useState(1);
  const global = usuario?.rol === 'superadmin' || usuario?.rol === 'contador';
  const veTasa = veTasa2(usuario);
  // La lista de usuarios (GET /usuarios) es de quien administra usuarios: al contador le respondía 403 y el filtro salía vacío (auditoría de frontend FE-08)
  const veUsuarios = puede('usuarios.admin');
  const filtros = { evento: evento || undefined, entidad: entidad || undefined, usuario_id: usuarioId || undefined, sucursal_id: sucursalId || undefined, desde: desde || undefined, hasta: hasta || undefined, pagina, limite: 25 };
  const q = useQuery({ queryKey: claves.auditoria(filtros), queryFn: () => get<Pagina>('/auditoria', filtros), placeholderData: (p) => p });
  const usuarios = useQuery({ queryKey: claves.usuarios, queryFn: () => get<UsuarioMin[]>('/usuarios'), enabled: veUsuarios });
  const sucursales = useQuery({ queryKey: claves.sucursales, queryFn: () => get<SucursalDTO[]>('/sucursales'), enabled: global });
  const exportar = () => {
    const qs = Object.entries(filtros).filter(([k, v]) => v !== undefined && k !== 'pagina' && k !== 'limite').map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
    descargarArchivo(`/auditoria/export${qs ? `?${qs}` : ''}`, 'auditoria.xlsx').then(() => toast.success('Auditoría exportada')).catch((e) => toast.error(mensajeDeError(e)));
  };
  const cambiar = (fn: () => void) => { fn(); setPagina(1); };
  return (
    <>
      <TituloPagina titulo="Auditoría" subtitulo="Quién, cuándo, qué, antes/después. Registro inmutable." acciones={<Button variant="outline" onClick={exportar} data-testid="auditoria-export"><FileSpreadsheet /> Exportar XLSX</Button>} />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Input placeholder="Evento (login, caja., config., ventas)" value={evento} onChange={(e) => cambiar(() => setEvento(e.target.value))} className="w-56" data-testid="auditoria-evento" />
        <Input placeholder="Entidad (documentos, config…)" value={entidad} onChange={(e) => cambiar(() => setEntidad(e.target.value))} className="w-48" data-testid="auditoria-entidad" />
        {veUsuarios ? <Select value={usuarioId} onChange={(e) => cambiar(() => setUsuarioId(e.target.value))} className="w-48" aria-label="Usuario" data-testid="auditoria-usuario"><option value="">Todos los usuarios</option>{(usuarios.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}</Select> : null}
        {global ? <Select value={sucursalId} onChange={(e) => cambiar(() => setSucursalId(e.target.value))} className="w-44" aria-label="Sucursal"><option value="">Todas las sucursales</option>{(sucursales.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}</Select> : null}
        <Input type="date" value={desde} onChange={(e) => cambiar(() => setDesde(e.target.value))} className="w-40" aria-label="Desde" />
        <Input type="date" value={hasta} onChange={(e) => cambiar(() => setHasta(e.target.value))} className="w-40" aria-label="Hasta" />
      </div>
      <EstadoQuery cargando={q.isLoading} error={q.error} datos={q.data} vacio={(d) => d.datos.length === 0} reintentar={() => void q.refetch()}>
        {(d) => (
          <div className="space-y-3">
            <Table data-testid="auditoria-tabla">
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Suc.</TableHead>
                  <TableHead>Evento</TableHead>
                  <TableHead>Entidad</TableHead>
                  <TableHead>Cambio (antes → después)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.datos.map((a) => (
                  <TableRow key={a.id} data-testid={`auditoria-${a.evento}`}>
                    <TableCell className="whitespace-nowrap text-xs">{formatearFechaHora(a.ocurrido_en, { second: '2-digit' })}</TableCell>
                    <TableCell>{a.usuario_nombre ?? '—'}</TableCell>
                    <TableCell>{a.sucursal_codigo ?? <span className="text-muted-foreground">global</span>}</TableCell>
                    <TableCell><Badge variant={a.evento.includes('bloqueo') || a.evento.includes('reuso') || a.evento.includes('reabrir') ? 'critico' : 'neutro'}>{a.evento}</Badge></TableCell>
                    <TableCell className="text-xs">{a.entidad}{a.entidad_id ? <span className="block text-[10px] text-muted-foreground">{a.entidad_id.slice(-12)}</span> : null}</TableCell>
                    <TableCell><Diff antes={a.antes} despues={a.despues} detalle={a.detalle} veTasa={veTasa} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{d.total} eventos · página {d.pagina}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={pagina * d.limite >= d.total} onClick={() => setPagina((p) => p + 1)}>Siguiente</Button>
              </div>
            </div>
          </div>
        )}
      </EstadoQuery>
    </>
  );
}
